import * as Sentry from "@sentry/react";
import { isSentryErrorReportingEnabled } from "../utils/sentryPreference";

const MAX_STRING_LENGTH = 500;
const MAX_BREADCRUMB_STRING_LENGTH = 80;
const SENSITIVE_KEY_PATTERN =
  /(?:password|passwd|secret|token|api[_-]?key|auth|authorization|cookie|dsn|xml|mjcf)/i;
const SAFE_BREADCRUMB_KEYS = new Set([
  "action",
  "area",
  "actuatorCount",
  "assetCount",
  "bodyCount",
  "cameraCount",
  "category",
  "contactExcludeCount",
  "contactPairCount",
  "durationMs",
  "engine",
  "equalityCount",
  "fileExtension",
  "fileMapEntryCount",
  "geomCount",
  "hfieldCount",
  "isBuiltinPreset",
  "jointCount",
  "loadMode",
  "mappedBodies",
  "materialCount",
  "messageType",
  "missingAssetCount",
  "operation",
  "phase",
  "presetCatalogId",
  "preserveActuatorControls",
  "resetScene",
  "sensorCount",
  "siteCount",
  "source",
  "startupPosePolicy",
  "status",
  "step",
  "textureCount",
  "tendonCount",
  "totalBodies",
  "uploadedFileCount",
]);

function scrubUrl(value) {
  try {
    const url = new URL(value, window.location.origin);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return value.split("?")[0].split("#")[0];
  }
}

function scrubString(value) {
  const withoutLocalPaths = value.replace(/[A-Za-z]:\\[^\s"'<>]+/g, "[local-path]");
  const withoutQueryStrings = withoutLocalPaths.replace(/https?:\/\/[^\s"'<>]+/g, (url) =>
    scrubUrl(url)
  );

  if (withoutQueryStrings.length > MAX_STRING_LENGTH) {
    return `${withoutQueryStrings.slice(0, MAX_STRING_LENGTH)}...[truncated]`;
  }

  return withoutQueryStrings;
}

function scrubValue(value, key = "") {
  if (value == null) {
    return value;
  }

  if (SENSITIVE_KEY_PATTERN.test(key)) {
    return "[redacted]";
  }

  if (typeof value === "string") {
    return scrubString(value);
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => scrubValue(item));
  }

  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, 30)
        .map(([entryKey, entryValue]) => [entryKey, scrubValue(entryValue, entryKey)])
    );
  }

  return value;
}

function scrubBreadcrumbString(value) {
  const scrubbed = scrubString(value);
  if (scrubbed.length <= MAX_BREADCRUMB_STRING_LENGTH) return scrubbed;
  return `${scrubbed.slice(0, MAX_BREADCRUMB_STRING_LENGTH)}...[truncated]`;
}

function scrubBreadcrumbValue(value) {
  if (value == null) return value;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") return scrubBreadcrumbString(value);
  return null;
}

function scrubBreadcrumbData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};

  return Object.fromEntries(
    Object.entries(data)
      .filter(([key]) => SAFE_BREADCRUMB_KEYS.has(key))
      .map(([key, value]) => [key, scrubBreadcrumbValue(value)])
      .filter(([, value]) => value !== null)
  );
}

function scrubEvent(event) {
  const scrubbed = scrubValue(event);

  if (scrubbed.request?.url) {
    scrubbed.request.url = scrubUrl(scrubbed.request.url);
  }

  if (scrubbed.user) {
    delete scrubbed.user;
  }

  return scrubbed;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn || !isSentryErrorReportingEnabled()) {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release:
      import.meta.env.VITE_SENTRY_RELEASE || `critter-factory@${import.meta.env.VITE_APP_VERSION}`,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    maxBreadcrumbs: 25,
    beforeBreadcrumb(breadcrumb) {
      if (!isSentryErrorReportingEnabled()) return null;
      return scrubValue(breadcrumb);
    },
    beforeSend(event) {
      if (!isSentryErrorReportingEnabled()) return null;
      return scrubEvent(event);
    },
  });
}

export function addBreadcrumb(message, data = {}, level = "info") {
  if (!isSentryErrorReportingEnabled()) return;

  Sentry.addBreadcrumb({
    category: "app",
    message: scrubBreadcrumbString(String(message || "app_event")),
    level,
    data: scrubBreadcrumbData(data),
  });
}

export function captureError(error, context) {
  if (!isSentryErrorReportingEnabled()) return;

  Sentry.captureException(error, {
    extra: scrubValue(context),
  });
}
